import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { FarmaError } from 'src/integracoes/modelos/erros';
import {
  obtemInfoFinanceiras,
  criarInfoFinanceira,
  atualizarInfoFinanceira,
  deletarInfoFinanceira,
  obtemDres
} from 'src/integracoes/servicos/mongo-atlas/funcoes/infoFinanceiras';
import {
  LIST_INFO_FINANCEIRAS_INIT,
  CREATE_INFO_FINANCEIRAS_INIT,
  UPDATE_INFO_FINANCEIRAS_INIT,
  DELETE_INFO_FINANCEIRAS_INIT,
  LIST_DRES_INIT
} from '../actionTypes';
import {
  listInfoFinanceirasSuccess,
  createInfoFinanceirasSuccess,
  updateInfoFinanceirasSuccess,
  deleteInfoFinanceirasSuccess,
  listDresSuccess,
  infoFinanceirasErrorMsg
} from './actions';
import { IInfoFinanceiro } from 'src/integracoes/servicos/mongo-atlas/modelos/IInfoFinanceiras';

interface FetchListaInfoFinanceirasResult {
  isError: boolean;
  infoFinanceiras: any[] | null;
  errorMessage: string | null;
}

interface FetchListaDresResult {
  isError: boolean;
  dres: any[] | null;
  errorMessage: string | null;
}


/* ------------------Lista Info Financeiras---------------- */
export const fetchListarInfoFinanceiras = async (
  empId: string,
  mesCompetencia?: string
): Promise<FetchListaInfoFinanceirasResult> => {
  try {
    const { status, dados, mensagem } = await obtemInfoFinanceiras(empId, mesCompetencia);

    if (status && Array.isArray(dados)) {
      return {
        isError: false,
        infoFinanceiras: dados,
        errorMessage: null,
      };
    }

    return {
      isError: true,
      infoFinanceiras: [],
      errorMessage: mensagem,
    };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseListarInfoFinanceiras({ payload }: any) {
  const { empId, mesCompetencia } = payload;

  try {
    const result: FetchListaInfoFinanceirasResult = yield call(
      fetchListarInfoFinanceiras,
      empId,
      mesCompetencia
    );

    if (!result.isError) {
      yield put(
        listInfoFinanceirasSuccess({ infoFinanceiras: result.infoFinanceiras })
      );
    } else {
      yield put(
        infoFinanceirasErrorMsg({ msg: result.errorMessage })
      );
    }
  } catch (error: any) {
    const mensagem = error.mensagem ? `${error.mensagem}` : `${error.message}`;
    yield put(infoFinanceirasErrorMsg({ msg: mensagem }));
  }
}

export function* watchListarInfoFinanceiras() {
  yield takeEvery(
    LIST_INFO_FINANCEIRAS_INIT,
    parseListarInfoFinanceiras
  );
}

/* ------------------Criar Info Financeira ---------------- */
export const fetchCriarInfoFinanceira = async (
  infoFinanceira: IInfoFinanceiro
) => {
  try {
    const { status, dados, mensagem } = await criarInfoFinanceira(
      infoFinanceira
    );

    if (status) {
      return {
        isCreated: true,
        msg: mensagem,
        data: dados,
      };
    }

    return {
      isCreated: false,
      msg: mensagem,
      data: null,
    };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseCriarInfoFinanceira({
  payload,
}: {
  payload: any;
  type: any;
}) {
  const { infoFinanceira } = payload;

  try {
    const { isCreated, msg, data } =
      yield call(fetchCriarInfoFinanceira, infoFinanceira);

    if (isCreated) {
      yield put(
        createInfoFinanceirasSuccess({
          data,
          msg,
        })
      );
    } else {
      yield put(infoFinanceirasErrorMsg({ msg }));
    }
  } catch (error: any) {
    const mensagem = error.mensagem ? `${error.mensagem}` : `${error.message}`;
    yield put(infoFinanceirasErrorMsg({ msg: mensagem }));
  }
}

export function* watchCriarInfoFinanceira() {
  yield takeEvery(
    CREATE_INFO_FINANCEIRAS_INIT,
    parseCriarInfoFinanceira
  );
}

/* ------------------Atualizar Info Financeira ---------------- */
export const fetchAtualizarInfoFinanceira = async (
  id: string,
  infoFinanceira: IInfoFinanceiro
) => {
  try {
    const { status, dados, mensagem } =
      await atualizarInfoFinanceira(id, infoFinanceira);

    if (status) {
      return {
        isUpdated: true,
        msg: mensagem,
        data: dados,
      };
    }

    return {
      isUpdated: false,
      msg: mensagem,
      data: null,
    };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseAtualizarInfoFinanceira({
  payload,
}: {
  payload: any;
  type: any;
}) {
  const { id, data } = payload;

  try {
    const { isUpdated, msg, data: updatedData } =
      yield call(fetchAtualizarInfoFinanceira, id, data);

    if (isUpdated) {
      yield put(
        updateInfoFinanceirasSuccess({
          data: updatedData,
          msg,
        })
      );
    } else {
      yield put(infoFinanceirasErrorMsg({ msg }));
    }
  } catch (error: any) {
    const mensagem = error.mensagem ? `${error.mensagem}` : `${error.message}`;
    yield put(infoFinanceirasErrorMsg({ msg: mensagem }));
  }
}

export function* watchAtualizarInfoFinanceira() {
  yield takeEvery(
    UPDATE_INFO_FINANCEIRAS_INIT,
    parseAtualizarInfoFinanceira
  );
}

/* ------------------Deletar Info Financeira ---------------- */
export const fetchDeletarInfoFinanceira = async (
  id: string
) => {
  try {
    const { status, mensagem } = await deletarInfoFinanceira(id);

    if (status) {
      return {
        isDeleted: true,
        msg: mensagem,
        id,
      };
    }

    return {
      isDeleted: false,
      msg: mensagem,
      id: null,
    };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseDeletarInfoFinanceira({
  payload,
}: {
  payload: any;
  type: any;
}) {
  const { id } = payload;

  try {
    const { isDeleted, msg, id: deletedId } =
      yield call(fetchDeletarInfoFinanceira, id);

    if (isDeleted) {
      yield put(
        deleteInfoFinanceirasSuccess({
          id: deletedId,
          msg,
        })
      );
    } else {
      yield put(infoFinanceirasErrorMsg({ msg }));
    }
  } catch (error: any) {
    const mensagem = error.mensagem ? `${error.mensagem}` : `${error.message}`;
    yield put(infoFinanceirasErrorMsg({ msg: mensagem }));
  }
}

export function* watchDeletarInfoFinanceira() {
  yield takeEvery(
    DELETE_INFO_FINANCEIRAS_INIT,
    parseDeletarInfoFinanceira
  );
}

/* ------------------Lista DREs---------------- */
export const fetchListarDres = async (): Promise<FetchListaDresResult> => {
  try {
    const { status, dados, mensagem } = await obtemDres();

    if (status && Array.isArray(dados)) {
      return {
        isError: false,
        dres: dados,
        errorMessage: null,
      };
    }

    return {
      isError: true,
      dres: [],
      errorMessage: mensagem,
    };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseListarDres() {
  try {
    const result: FetchListaDresResult = yield call(
      fetchListarDres
    );

    if (!result.isError) {
      yield put(listDresSuccess({ dres: result.dres }));
    } else {
      yield put(infoFinanceirasErrorMsg({ msg: result.errorMessage }));
    }
  } catch (error: any) {
    const mensagem = error.mensagem ? `${error.mensagem}` : `${error.message}`;
    yield put(infoFinanceirasErrorMsg({ msg: mensagem }));
  }
}

export function* watchListarDres() {
  yield takeEvery(
    LIST_DRES_INIT,
    parseListarDres
  );
}

export default function* rootSaga() {
  yield all([
    fork(watchListarInfoFinanceiras),
    fork(watchCriarInfoFinanceira),
    fork(watchAtualizarInfoFinanceira),
    fork(watchDeletarInfoFinanceira),
    fork(watchListarDres)
  ]);
}
